import React, { useState } from "react";
import { toast } from "react-toastify";
import { useParams } from "react-router";
import { useLocation } from "react-router-dom";
import codeyoung from "../../assets/images/primary_logo.png";
import submitIcon from "../../assets/images/submitIcon.png";
import { SvgIcons } from "../SharedComponents/SvgIcons/SvgIcons";
import { useFirstClassQuestions } from "./hooks/useFirstClassQuestions";
import { useSubmitFirstClassFeedback } from "./hooks/useSubmitFirstClassFeedback";
import classes from "./FirstClassFeedback.module.css";

toast.configure();


const RATING_LABELS = ["Good", "Average", "Bad"];
const RATING_EMOJI = { Good: "😄", Average: "🙂", Bad: "😟" };

const RATING_STATE_COLORS = {
  Good: { bg: "#EDF7EE", border: "#3C8B3F", text: "#3C8B3F" },
  Average: { bg: "#FFF9E0", border: "#E07800", text: "#E07800" },
  Bad: { bg: "#FFF2F1", border: "#E2483D", text: "#E2483D" },
};
const RATING_CHECK_ICON = {
  Good: "check_circle_good",
  Average: "check_circle_average",
  Bad: "check_circle_bad",
};

function PageShell({ children, centered = false, footer = null }) {
  return (
    <div className={classes.page}>
      <header className={classes.header}>
        <img src={codeyoung} alt="codeyoung" className={classes.logoImg} />
      </header>
      <div
        className={`${classes.body} ${centered ? classes.bodyCentered : ""}`}
      >
        {children}
      </div>
      <footer className={classes.footer}>
        {footer && <div className={classes.footerInner}>{footer}</div>}
      </footer>
    </div>
  );
}

function FirstClassFeedback() {
  const params = useParams();
  const location = useLocation();
  const source = new URLSearchParams(location.search).get("source");

  const [responses, setResponses] = useState({});
  const [linkState, setLinkState] = useState(null); // null | "invalid" | "alreadyRecorded" | "success"

  const { apiData, loading: questionsLoading, error: questionsError } =
    useFirstClassQuestions();
  const { mutate: submitFeedback, loading: submitting } =
    useSubmitFirstClassFeedback();

  const questions = (apiData?.questions || [])
    .slice()
    .sort((a, b) => a.sequence - b.sequence);

  const onAnswerChange = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const mandatoryQuestions = questions.filter((q) => q.mandatory);

  const hasMissingMandatory = mandatoryQuestions.some(
    (q) => !responses[q.questionId],
  );

  const handleSubmit = async () => {
    if (hasMissingMandatory) return;

    const body = {
      orderId: params.orderId,
      scheduleId: params.scheduleId,
      type: 1,
      responses,
    };
    if (source) body.source = source;

    try {
      await submitFeedback({ data: body });
      setLinkState("success");
    } catch (e) {
      const msg = e?.response?.data?.msg;
      if (msg === "Invalid URL!") {
        setLinkState("invalid");
      } else if (msg === "Feedback already recorded!") {
        setLinkState("alreadyRecorded");
      } else {
        toast.error(msg || "Something went wrong. Please try again.", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    }
  };

  if (linkState === "invalid") {
    return (
      <PageShell centered>
        <h2>This link is invalid or has expired</h2>
      </PageShell>
    );
  }

  if (linkState === "alreadyRecorded") {
    return (
      <PageShell centered>
        <h2>You've already shared feedback for this class — thank you!</h2>
      </PageShell>
    );
  }

  if (linkState === "success") {
    return (
      <PageShell centered>
        <div className={classes.card}>
          <img src={submitIcon} alt="" className={classes.successEmoji} />
          <span className={classes.successHeading}>
            Thank you for your feedback!
          </span>
          <span className={classes.successSubHeading}>
            Your feedback helps us create a better learning experience for
            your child.
          </span>
        </div>
      </PageShell>
    );
  }

  if (questionsError) {
    return (
      <PageShell centered>
        <h2>Something went wrong. Please try again later.</h2>
      </PageShell>
    );
  }

  return (
    <PageShell
      footer={
        <button
          className={classes.btn}
          onClick={handleSubmit}
          disabled={questionsLoading || submitting || hasMissingMandatory}
        >
          {submitting ? "Submitting..." : "Submit feedback"}
        </button>
      }
    >
      <div className={classes.card}>
        <span className={classes.headingText}>
          Tell us about your child&apos;s{" "}
          <span className={classes.headingHighlight}>
            first class experience
          </span>
        </span>
        <span className={classes.subHeadingText}>
          Your feedback helps us improve future learning experiences. It
          takes less than a minute.
        </span>

        {!questionsLoading && (
          <>
            <div className={classes.progressBar}>
              {mandatoryQuestions.map((question) => (
                <span
                  key={question.questionId}
                  className={`${classes.progressSegment} ${
                    responses[question.questionId]
                      ? classes.progressSegmentFilled
                      : ""
                  }`}
                />
              ))}
            </div>

            {questions.map((question) => (
              <div className={classes.questionBlock} key={question.questionId}>
                <span className={classes.questionLabel}>
                  {question.questionText}
                  {question.mandatory ? (
                    <span className={classes.requiredMark}>*</span>
                  ) : null}
                </span>

                {question.maxRating > 0 ? (
                  <div className={classes.optionsRow}>
                    {RATING_LABELS.slice(0, question.maxRating).map(
                      (option) => {
                        const isSelected =
                          responses[question.questionId] === option;
                        const stateColors = RATING_STATE_COLORS[option];
                        return (
                          <div
                            key={option}
                            role="button"
                            tabIndex={0}
                            className={classes.optionCard}
                            style={
                              isSelected
                                ? {
                                  background: stateColors.bg,
                                  borderColor: stateColors.border,
                                  borderWidth: "2px",
                                }
                                : undefined
                            }
                            onClick={() =>
                              onAnswerChange(question.questionId, option)
                            }
                          >
                            {isSelected && (
                              <SvgIcons
                                name={RATING_CHECK_ICON[option]}
                                width="12"
                                height="12"
                                className={classes.optionCheckBadge}
                              />
                            )}
                            <span className={classes.optionEmoji}>
                              {RATING_EMOJI[option]}
                            </span>
                            <span
                              className={classes.optionLabel}
                              style={
                                isSelected
                                  ? { color: stateColors.text }
                                  : undefined
                              }
                            >
                              {option}
                            </span>
                          </div>
                        );
                      },
                    )}
                  </div>
                ) : (
                  <textarea
                    rows="4"
                    className={classes.textArea}
                    placeholder="Enter comments here..."
                    value={responses[question.questionId] || ""}
                    onChange={(e) =>
                      onAnswerChange(question.questionId, e.target.value)
                    }
                  />
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </PageShell>
  );
}

export default FirstClassFeedback;
